import { FlaskConical } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { endpoints, type ServerConfig } from '../services/api';
import { startMicrosoft, useSession } from '../store/session';
import { PairWithPhone } from './PairWithPhone';
import { SignInForm } from './settings/AccountTab';
import { buttonClass, ghostButtonClass } from './settings/controls';

const LIVE_SITE = 'https://canvas.kcl.cloud';

/** "STAGING" ribbon on test sites, so a test screen is never mistaken for the real one. */
export function StagingBanner({ env }: { env: string | undefined }) {
  if (!env || env === 'production' || env === 'offline') return null;
  return (
    <div className="pointer-events-none fixed left-1/2 top-0 z-[60] -translate-x-1/2 rounded-b-lg bg-amber-500 px-3 py-0.5 text-[11px] font-bold uppercase tracking-widest text-black shadow">
      {env} · test only
    </div>
  );
}

/**
 * On a private test site (STAGING_ALLOWED_EMAILS), shows a sign-in page to anyone who isn't
 * one of the allowed accounts, instead of the app.
 */
export function StagingGate({ children }: { children: ReactNode }) {
  // undefined = still checking; null = couldn't reach the server (show the app – it works offline).
  const [config, setConfig] = useState<ServerConfig | null | undefined>(undefined);
  const user = useSession((s) => s.user);

  useEffect(() => {
    endpoints
      .config()
      .then(setConfig)
      .catch(() => setConfig(null));
  }, [user]); // re-check after signing in

  // Don't mount the app (which starts loading media and feeds) until we know who may see it.
  if (config === undefined) return null;
  if (config?.access !== 'denied') {
    return (
      <>
        <StagingBanner env={config?.env} />
        {children}
      </>
    );
  }
  return <PrivateSite />;
}

function PrivateSite() {
  const [pairing, setPairing] = useState(false);
  const authError = new URLSearchParams(window.location.search).get('auth_error');
  return (
    <div className="h-full overflow-y-auto bg-[var(--bg)]">
      <StagingBanner env="staging" />
      <main className="mx-auto max-w-md space-y-6 px-6 py-16">
        <div className="flex items-center gap-3">
          <FlaskConical className="h-7 w-7 text-[var(--accent)]" aria-hidden />
          <h1 className="text-2xl font-semibold">Private test site</h1>
        </div>
        <p className="text-sm">
          This is KCL Canvas’s test site, open to invited accounts only. To use KCL Canvas, go to{' '}
          <a href={LIVE_SITE} className="text-[var(--accent)] hover:underline">
            canvas.kcl.cloud
          </a>
          .
        </p>
        {authError === 'restricted' && <p className="text-sm text-red-400">That account isn’t invited to the test site.</p>}
        <a href={LIVE_SITE} className={buttonClass}>
          Go to KCL Canvas
        </a>
        <section className="glass space-y-3 p-4">
          <h2 className="font-semibold">Invited? Sign in</h2>
          <button className={ghostButtonClass} onClick={() => startMicrosoft('signin')}>
            Continue with Microsoft
          </button>
          <SignInForm />
          {pairing ? (
            <PairWithPhone onCancel={() => setPairing(false)} />
          ) : (
            <button className={ghostButtonClass} onClick={() => setPairing(true)}>
              On a TV? Sign in with your phone
            </button>
          )}
        </section>
      </main>
    </div>
  );
}
